import type { Node as PMNode } from 'prosemirror-model';
import { getInlineDiff, tokenizeInlineContent, type InlineDiffToken, type InlineDiffResult } from './inline-diffing';
import { getAttributesDiff, type AttributesDiff } from './attributes-diffing';
import { getInsertionPos, type NodePositionInfo } from './diff-utils';
import { normalizeParagraphAttrs, normalizeParagraphNodeJSON, semanticInlineNodeKey } from './semantic-normalization';
import { levenshteinDistance } from './similarity';

// Heuristics that prevent unrelated paragraphs from being paired as modifications.
const SIMILARITY_THRESHOLD = 0.65;
// Higher threshold for sequences with repeated-content paragraphs (legal boilerplate, etc.).
// Repeated paragraphs share incidental character overlap without being semantically related edits.
const REPEATED_CONTENT_SIMILARITY_THRESHOLD = 0.7;
const MIN_LENGTH_FOR_SIMILARITY = 4;

type NodeJSON = ReturnType<PMNode['toJSON']>;

export interface ParagraphNodeInfo {
  /** ProseMirror paragraph node reference. */
  node: PMNode;
  /** Absolute position of the paragraph in the document. */
  pos: number;
  /** Depth of the paragraph within the document tree. */
  depth: number;
  /** Flattened inline tokens for inline diffing. */
  text: InlineDiffToken[];
  /** Absolute end position used for trailing inserts. */
  endPos: number;
  /** Plain-text representation of the paragraph content. */
  fullText: string;
  /** Semantic fingerprint of all inline content (text + nodes), used for identity matching. */
  contentSignature: string;
}

/**
 * Base shape shared by every paragraph diff payload.
 */
interface ParagraphDiffBase<Action extends 'added' | 'deleted' | 'modified'> {
  /** Change type for this paragraph. */
  action: Action;
  /** Node type name (always `paragraph`). */
  nodeType: 'paragraph';
  /** Anchor position in the old document for replaying diffs. */
  pos: number;
}

/**
 * Diff payload produced when a paragraph is inserted.
 */
type AddedParagraphDiff = ParagraphDiffBase<'added'> & {
  /** Serialized paragraph payload inserted into the document. */
  nodeJSON: NodeJSON;
  /** Plain-text content of the inserted paragraph. */
  text: string;
};

/**
 * Diff payload produced when a paragraph is deleted.
 */
type DeletedParagraphDiff = ParagraphDiffBase<'deleted'> & {
  /** Serialized paragraph payload removed from the document. */
  nodeJSON: NodeJSON;
  /** Plain-text content of the removed paragraph. */
  oldText: string;
};

/**
 * Diff payload emitted when a paragraph changes, including inline edits.
 */
type ModifiedParagraphDiff = ParagraphDiffBase<'modified'> & {
  /** Serialized paragraph payload before the change. */
  oldNodeJSON: NodeJSON;
  /** Serialized paragraph payload after the change. */
  newNodeJSON: NodeJSON;
  /** Plain-text content before the change. */
  oldText: string;
  /** Plain-text content after the change. */
  newText: string;
  /** Inline diff operations within the paragraph. */
  contentDiff: InlineDiffResult[];
  /** Attribute-level diff for the paragraph. */
  attrsDiff: AttributesDiff | null;
};

/**
 * Union of every diff variant the paragraph diffing logic can produce.
 */
export type ParagraphDiff = AddedParagraphDiff | DeletedParagraphDiff | ModifiedParagraphDiff;

/**
 * Creates a reusable snapshot that stores flattened paragraph content plus position metadata.
 *
 * @param paragraph Paragraph node to flatten.
 * @param paragraphPos Position of the paragraph in the document.
 * @param depth Depth of the paragraph within the document tree.
 * @returns Snapshot containing tokens (with offsets) and derived metadata.
 */
export function createParagraphSnapshot(paragraph: PMNode, paragraphPos: number, depth: number): ParagraphNodeInfo {
  const text = tokenizeInlineContent(paragraph, paragraphPos + 1);
  return {
    node: paragraph,
    pos: paragraphPos,
    depth,
    text,
    endPos: paragraphPos + 1 + paragraph.content.size,
    fullText: text
      .map((token) => {
        if (token.kind === 'text') return token.text;
        // Recover visible text from atomic inline containers (structuredContent)
        // so that paragraph similarity scoring and oldText/newText summaries
        // include SDT content rather than treating the control as empty.
        if (token.kind === 'inlineNode' && token.nodeType === 'structuredContent') {
          return token.node.textContent;
        }
        return '';
      })
      .join(''),
    contentSignature: buildContentSignature(text),
  };
}

/**
 * Builds a semantic fingerprint from inline tokens that covers both
 * text characters and inline nodes (images, etc.).
 *
 * Text-only paragraphs produce the same result as `fullText`.
 * Image-only paragraphs produce a unique key per distinct image,
 * so that the paragraph comparator can tell them apart.
 */
function buildContentSignature(tokens: InlineDiffToken[]): string {
  return tokens
    .map((token) => {
      if (token.kind === 'text') {
        return token.text;
      }
      // Null bytes delimit inline node keys so they can't collide with text
      return `\0${semanticInlineNodeKey(token.node)}\0`;
    })
    .join('');
}

/**
 * Determines whether equal paragraph nodes should still be marked as modified because their serialized structure differs.
 *
 * @param oldParagraph Previous paragraph node reference.
 * @param newParagraph Updated paragraph node reference.
 * @returns True when the serialized JSON payload differs.
 */
export function shouldProcessEqualAsModification(
  oldParagraph: ParagraphNodeInfo,
  newParagraph: ParagraphNodeInfo,
): boolean {
  const oldNormalized = normalizeParagraphNodeJSON(oldParagraph.node.toJSON());
  const newNormalized = normalizeParagraphNodeJSON(newParagraph.node.toJSON());
  return JSON.stringify(oldNormalized) !== JSON.stringify(newNormalized);
}

/**
 * Compares two paragraphs for identity based on paraId, then content signature.
 *
 * The content signature covers both text and inline nodes (images, etc.),
 * so image-only paragraphs with different images are not falsely paired.
 *
 * Identity is gated on structural context (depth) first. Cross-depth pairing
 * via paraId or content-signature alone produces false identity when DOCX
 * importers renumber paraIds across structural changes, e.g. a table-cell
 * paragraph and a body paragraph sharing a paraId or both being empty.
 */
export function paragraphComparator(oldParagraph: ParagraphNodeInfo, newParagraph: ParagraphNodeInfo): boolean {
  if (oldParagraph?.depth !== newParagraph?.depth) {
    return false;
  }
  const oldId = oldParagraph?.node?.attrs?.paraId;
  const newId = newParagraph?.node?.attrs?.paraId;
  if (oldId && newId && oldId === newId) {
    return true;
  }
  // Content signature includes inline node fingerprints, so it distinguishes
  // image-only paragraphs that would otherwise all have empty fullText.
  const oldSig = oldParagraph?.contentSignature ?? oldParagraph?.fullText;
  const newSig = newParagraph?.contentSignature ?? newParagraph?.fullText;
  return oldSig === newSig;
}

/**
 * Builds a normalized payload describing a paragraph addition, ensuring all consumers receive the same metadata shape.
 */
export function buildAddedParagraphDiff(
  paragraph: ParagraphNodeInfo,
  oldNodes?: readonly NodePositionInfo[],
  oldIdx?: number,
): AddedParagraphDiff {
  return {
    action: 'added',
    nodeType: 'paragraph',
    nodeJSON: paragraph.node.toJSON(),
    text: paragraph.fullText,
    pos: getInsertionPos(paragraph.depth, oldNodes, oldIdx),
  };
}

/**
 * Builds a normalized payload describing a paragraph deletion so diff consumers can show removals with all context.
 */
export function buildDeletedParagraphDiff(paragraph: ParagraphNodeInfo): DeletedParagraphDiff {
  return {
    action: 'deleted',
    nodeType: 'paragraph',
    nodeJSON: paragraph.node.toJSON(),
    oldText: paragraph.fullText,
    pos: paragraph.pos,
  };
}

/**
 * Builds the payload for a paragraph modification, including text-level diffs, so renderers can highlight edits inline.
 */
export function buildModifiedParagraphDiff(
  oldParagraph: ParagraphNodeInfo,
  newParagraph: ParagraphNodeInfo,
): ModifiedParagraphDiff | null {
  const contentDiff = getInlineDiff(oldParagraph.text, newParagraph.text, oldParagraph.endPos);

  const attrsDiff = getAttributesDiff(
    normalizeParagraphAttrs(oldParagraph.node.attrs),
    normalizeParagraphAttrs(newParagraph.node.attrs),
  );
  if (contentDiff.length === 0 && !attrsDiff) {
    return null;
  }

  return {
    action: 'modified',
    nodeType: 'paragraph',
    oldNodeJSON: oldParagraph.node.toJSON(),
    newNodeJSON: newParagraph.node.toJSON(),
    oldText: oldParagraph.fullText,
    newText: newParagraph.fullText,
    pos: oldParagraph.pos,
    contentDiff,
    attrsDiff,
  };
}

/**
 * Decides whether a delete/insert pair should be reinterpreted as a modification to minimize noisy diff output.
 *
 * @param hasRepeatedContent When true, applies a stricter similarity threshold to avoid false pairings
 *   in documents with many repeated paragraphs (e.g. legal boilerplate). Repeated paragraphs share
 *   incidental character overlap that would otherwise pull unrelated paragraphs into a "modified" pairing.
 */
export function canTreatAsModification(
  oldParagraph: ParagraphNodeInfo,
  newParagraph: ParagraphNodeInfo,
  hasRepeatedContent = false,
): boolean {
  if (oldParagraph?.depth !== newParagraph?.depth) {
    return false;
  }
  if (paragraphComparator(oldParagraph, newParagraph)) {
    return true;
  }

  const oldText = oldParagraph.fullText;
  const newText = newParagraph.fullText;
  const maxLength = Math.max(oldText.length, newText.length);
  if (maxLength < MIN_LENGTH_FOR_SIMILARITY) {
    return false;
  }

  const threshold = hasRepeatedContent ? REPEATED_CONTENT_SIMILARITY_THRESHOLD : SIMILARITY_THRESHOLD;
  const similarity = getTextSimilarityScore(oldText, newText);
  return similarity >= threshold;
}

/**
 * Scores the similarity between two text strings so the diff can decide if they represent the same conceptual paragraph.
 */
function getTextSimilarityScore(oldText: string, newText: string): number {
  if (!oldText && !newText) {
    return 1;
  }

  const distance = levenshteinDistance(oldText, newText);
  const maxLength = Math.max(oldText.length, newText.length) || 1;
  return 1 - distance / maxLength;
}
